/**
 * Resolving an interaction's RxCUI to the medication's own display name.
 *
 * `/api/check-interactions` answers in RxCUIs by contract, so every surface
 * that shows a finding has to map `medication_in_rxcui` back to one of the
 * member's own medications before rendering it. That step used to be copied
 * into each surface, and the dashboard rebuild (STACK-515) rewrote the mapping
 * without re-creating it — so the interaction modal rendered `Medication (10582)`
 * where it had read "Levothyroxine" (STACK-540). One module, so a surface can
 * only get this wrong by not calling it.
 *
 * Lifted verbatim from `SupplementsTable`'s `getUserFriendlyMedicationNameWithData`
 * so that surface's behaviour is unchanged: one pass over the list, matching
 * `identifiers.in_rxcui` or the legacy `identifiers.rxcui`, each as a string
 * and as a number, because stored medications carry both spellings.
 */

/**
 * What a surface shows for an RxCUI that is genuinely not in the member's
 * medications. Ruled in STACK-540: this stays, and it must never be the normal
 * path. Module-local rather than exported — `StackCard` and
 * `RecommendedStackCard` each carry their own copy of this literal as a last
 * resort, and unifying the three is a separate change (see the PR).
 */
const fallbackLabel = (medicationInRxcui) => `Medication (${medicationInRxcui})`;

/**
 * The member-facing name for an interaction's medication.
 *
 * @param {string|number} medicationInRxcui - `medication_in_rxcui` from a finding
 * @param {Array} medications - the member's medications (`userProfile.medications`)
 * @returns {string} the medication's display name, else the fallback label
 */
export const getMedicationDisplayName = (medicationInRxcui, medications) => {
  if (!medicationInRxcui || !medications?.length) {
    return fallbackLabel(medicationInRxcui);
  }

  // Stored medications carry the identifier as a string on some records and a
  // number on others, so each candidate is compared both ways rather than
  // normalising one side and trusting it.
  const searchRxcui =
    typeof medicationInRxcui === 'string' ? parseInt(medicationInRxcui) : medicationInRxcui;

  const matchingMed = medications.find((med) => {
    const medInRxcui = med?.identifiers?.in_rxcui;
    const medRxcui = med?.identifiers?.rxcui; // legacy records predate in_rxcui

    const numericMedInRxcui =
      typeof medInRxcui === 'string' ? parseInt(medInRxcui) : medInRxcui;
    const numericMedRxcui = typeof medRxcui === 'string' ? parseInt(medRxcui) : medRxcui;

    return Boolean(
      (medInRxcui && medInRxcui.toString() === medicationInRxcui.toString()) ||
        (numericMedInRxcui && numericMedInRxcui === searchRxcui) ||
        (medRxcui && medRxcui.toString() === medicationInRxcui.toString()) ||
        (numericMedRxcui && numericMedRxcui === searchRxcui)
    );
  });

  return matchingMed?.name?.display || fallbackLabel(medicationInRxcui);
};
